<script
  setup
  lang="ts"
  generic="
    TProduct extends
      | Product
      | EventsOverviewProduct
      | (EventsOverviewProduct & { eventName?: string })
  "
>
import chunk from "lodash/chunk";
import type { Event, EventsOverviewProduct, Product } from "mws-ts-v2-sdk";

type Props = {
  products: TProduct[];
  event?: Event;
  showEventName?: boolean;
  showSkeleton?: boolean;
  hasMargin?: boolean;
  virtualisation?: boolean;
  gridAmount?: "default" | 4 | 2;
  trackingSource: string;
};

const {
  event,
  virtualisation,
  products,
  gridAmount = "default",
} = defineProps<Props>();

type Emits = {
  (e: "product-finished"): void;
  (e: "product-clicked", product: TProduct): void;
};

defineEmits<Emits>();

const skeletonItemsToShow = computed(() => {
  return typeof gridAmount === "number" ? gridAmount : 4;
});

const groupedProducts = computed(() => {
  if (!virtualisation) return [];

  return chunk(products, 20).map((chunkedProducts, index) => ({
    id: index,
    products: chunkedProducts,
  }));
});

const getProductEventName = (product: TProduct) =>
  "eventName" in product ? product.eventName : undefined;
</script>

<template>
  <ProductCardGridLayout
    v-if="showSkeleton"
    :has-margin="hasMargin"
    :grid-amount="gridAmount"
  >
    <ProductCardSkeleton
      v-for="index in skeletonItemsToShow"
      :key="index"
    />
  </ProductCardGridLayout>

  <ProductCardGridLayout
    v-else
    :has-margin="hasMargin"
    :grid-amount="gridAmount"
  >
    <template v-if="virtualisation">
      <BaseVirtualizedGridItems
        v-for="productsGroup in groupedProducts"
        :key="productsGroup.id"
        :items="productsGroup.products"
      >
        <template #default="{ item, visible }">
          <ProductCard
            v-if="visible"
            :product="item"
            :event="event"
            :show-event-name="showEventName"
            :event-name="getProductEventName(item)"
            class="products-grid__product-card"
            :tracking-source="trackingSource"
            @product-clicked="$emit('product-clicked', item)"
            @product-finished="$emit('product-finished')"
          />
        </template>
      </BaseVirtualizedGridItems>
    </template>
    <template v-else>
      <ProductCard
        v-for="product in products"
        :key="product.id"
        :product="product"
        :event="event"
        :show-event-name="showEventName"
        :event-name="getProductEventName(product)"
        :tracking-source="trackingSource"
        data-cy="product-card"
        class="products-grid__product-card"
        @product-clicked="$emit('product-clicked', product)"
        @product-finished="$emit('product-finished')"
      />
    </template>
  </ProductCardGridLayout>
</template>
